import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container, IconButton, Typography, useTheme } from '@mui/material';
import IncidentTile from '@ui/components/IncidentTile';
import SectionHeader from '@ui/components/SectionHeader';
import useHistoryListStyles from './useHistoryListStyles';
import { toast } from 'react-toastify';
import incidentsApi from '@application/api/endpoints/incident-api';
import { useTranslation } from 'react-i18next';
import useHeaderStyles from '@ui/layout/Layout/components/Header/useHeaderStyles';
import { SearchInput } from '@ui/components/common';
import { IosArrowLtr24Regular } from '@fluentui/react-icons';
import CarImage from '@public/assets/images/no-preview.png';
import { formatDMY } from '@application/utilities/date-time-formater';
const buttonSortOptions = [
    { label: 'Newest - Oldest', value: 'dateNewestOldest', category: 'Date' },
    { label: 'Oldest - Newest', value: 'dateOldestNewest', category: 'Date' },
    { label: 'High - Low', value: 'scoreHighLow', category: 'Score' },
    { label: 'Low - High', value: 'scoreLowHigh', category: 'Score' }
];
const HistoryList = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isLightMode = theme.palette.mode === 'light';
    const { containerStyles, contentContainerStyles } = useHistoryListStyles();
    const [incidentPreviewData, setIncidentPreviewData] = useState([]);
    const [selectedSortOption, setSelectedSortOption] = useState('dateNewestOldest');
    const { title } = useParams();
    const { t } = useTranslation();
    const [sortValue, setSortValue] = useState('dateNewestOldest');
    const [filteredIncidents, setFilteredIncidents] = useState([]);
    const { id } = useParams();
    const { MobileHeaderContainer, SearchBarContainer, NavTitleContainer, NavInner } = useHeaderStyles();
    const [filterValues, setFilterValues] = useState({
        minScore: 1,
        maxScore: 100,
        searchText: null,
        // startDate: null,
        // endDate: null,
        location: null,
        licensePlate: title,
        incidentId: null
    });
    const FetchIncidentsPreviewDetails = async () => {
        try {
            if (filterValues.licensePlate === 'manual') {
                filterValues.incidentId = id;
            }
            var response = await incidentsApi.fetchIncidentsPreviewHistory(filterValues);
            if (response.isSuccess) {
                setIncidentPreviewData(response.data);
            }
        }
        catch (error) {
            toast.error(t('SomethingWentWrong'));
        }
        finally {
        }
    };
    const RefreshIncidentsPreviewDetails = async () => {
        try {
            const initialFilterValues = {
                minScore: 1,
                maxScore: 100,
                searchText: null,
                location: null,
                licensePlate: title
            };
            var response = await incidentsApi.fetchIncidentsPreviewHistory(initialFilterValues);
            if (response.isSuccess) {
                setIncidentPreviewData(response.data);
            }
        }
        catch (error) {
            toast.error(t('SomethingWentWrong'));
        }
        finally {
        }
    };
    useEffect(() => {
        FetchIncidentsPreviewDetails();
    }, []);
    const handleIncidentTileClick = (id) => {
        if (id)
            navigate(`/incident-history/${id}/details`);
    };
    useEffect(() => {
        const sortedIncidents = sortIncidents(incidentPreviewData || [], selectedSortOption);
        setIncidentPreviewData(sortedIncidents);
        sortedIncidents;
    }, [selectedSortOption]);
    useEffect(() => {
        if (!incidentPreviewData)
            return;
        if (filterValues?.searchText?.trim() === '') {
            setFilteredIncidents(incidentPreviewData);
        }
        else {
            var searchIncidents = incidentPreviewData?.filter((x) => x.licensePlate.toLowerCase().includes(filterValues?.searchText?.toLowerCase() || ''));
            setFilteredIncidents(searchIncidents);
        }
    }, [filterValues.searchText, incidentPreviewData]);
    const sortIncidents = (incidents, sortBy) => {
        const sorted = [...incidents];
        switch (sortBy) {
            case 'dateNewestOldest':
                return sorted.sort((a, b) => new Date(b.incidentDate).getTime() - new Date(a.incidentDate).getTime());
            case 'dateOldestNewest':
                return sorted.sort((a, b) => new Date(a.incidentDate).getTime() - new Date(b.incidentDate).getTime());
            case 'scoreHighLow':
                return sorted.sort((a, b) => b.score - a.score);
            case 'scoreLowHigh':
                return sorted.sort((a, b) => a.score - b.score);
            case 'locationAZ':
                return sorted.sort((a, b) => (a.location || '').localeCompare(b.location || ''));
            case 'locationZA':
                return sorted.sort((a, b) => (b.location || '').localeCompare(a.location || ''));
            default:
                return sorted;
        }
    };
    const handleChangeSearch = ({ target: { value } }) => {
        setFilterValues((curr) => ({ ...curr, searchText: value }));
    };
    const handleBackClick = () => {
        navigate(-1); // Navigate back
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: MobileHeaderContainer, className: 'mobile-nav-container', children: [_jsx(Box, { className: 'nav-inner', sx: NavInner, children: _jsxs(Box, { sx: NavTitleContainer, className: 'nav-title', children: [_jsx(IconButton, { "aria-label": 'back', onClick: handleBackClick, sx: { p: 0 }, children: _jsx(IosArrowLtr24Regular, { fontSize: 24, color: isLightMode ? theme.palette.common.white : theme.palette.grey[900] }) }), _jsx(Typography, { variant: 'h4', className: 'mobile-nav-title', color: isLightMode ? theme.palette.common.white : theme.palette.grey[900], children: "Incident History" })] }) }), _jsx(Box, { className: 'searchbar-container', sx: SearchBarContainer, children: _jsx(SearchInput, { value: filterValues.searchText, onChange: handleChangeSearch }) })] }), _jsxs(Container, { id: 'page-history-list', sx: containerStyles, children: [_jsx(SectionHeader, { title: 'Incident List', showRangeSlider: false, showScoreRangeSlider: true, showLocationField: true, setSortValue: setSortValue, setFilterValues: setFilterValues, sortValue: sortValue, filterValues: filterValues, buttonSortOptions: buttonSortOptions, onApplyFilters: FetchIncidentsPreviewDetails, setSelectedSortOption: setSelectedSortOption, resetFilter: RefreshIncidentsPreviewDetails }), _jsx(Box, { sx: contentContainerStyles, className: 'history-list-content-container', children: filteredIncidents?.map(({ id, licensePlate, score, incidentDate, location, incidentTime, imageUrl }) => (_jsx(IncidentTile, { onTileClick: () => handleIncidentTileClick(id), title: licensePlate, incidentCount: score, date: formatDMY(incidentDate), time: incidentTime, venue: location, imgSrc: imageUrl
                                ? `${process.env.BLOB_BASE_URL}${process.env.FOLDER_NAME}/Images/${imageUrl}`
                                : CarImage }, id))) })] })] }));
};
export default HistoryList;
